import {ReportDriveLog} from "@/shared/timr";
import {TrackData, RecordingMapViewer} from "@/shared/recording/recordingMapViewer";
import {debounce} from "@/shared/utils/misc";

// L (Leaflet) is loaded globally
declare const L: typeof import("leaflet");

export interface DriveLogMapConfig {
	container: JQuery;
}

export class DriveLogMap {

	private map: L.Map;
	private mapIsMoving = false;
	private tracks: { [id: string]: TrackData } = {};
	private driveLogLoadQueue: ReportDriveLog[] = [];

	constructor(public config: DriveLogMapConfig) {
	}

	public clearDriveLogs() {
		this.setDriveLogs([]);
	}

	public setDriveLogs(driveLogs: ReportDriveLog[]) {
		this.clearAllTracks();
		if (driveLogs.length > 0) {
			this.showDriveLogs(driveLogs);
		} else {
			this.hideMap();
		}
	}

	public showDriveLogs(driveLogs: ReportDriveLog[]) {
		this.driveLogLoadQueue = this.driveLogLoadQueue.concat(driveLogs);
		this.loadNextDriveLogFromQueue();
	}

	public toggleDriveLog(driveLog: ReportDriveLog, visible: boolean) {
		const track = this.tracks[driveLog.id];
		if (visible && !track) {
			this.loadTrack(driveLog);
		} else if (!visible && track) {
			this.removeTrack(track);
			delete this.tracks[driveLog.id];
			this.setNeedsFitMapBounds();
			if (!Object.keys(this.tracks).length) {
				this.hideMap();
			}
		}
	}

	private loadNextDriveLogFromQueue() {
		if (this.driveLogLoadQueue.length > 0) {
			this.toggleDriveLog(this.driveLogLoadQueue.shift(), true);
		}
	}

	private loadTrack(driveLog: ReportDriveLog) {
		if (!driveLog.hasTrack) {
			return;
		}
		const recordingMapViewer = new RecordingMapViewer({});	// Empty config
		recordingMapViewer.loadTrack(driveLog, {
			trackLoadedCallback: (latlngs, dl, config) => {
				this.showMap((map) => {
					recordingMapViewer.displayTrack(map, latlngs, dl, config);
				});
			},
			trackViewCallback: (trackData) => {
				this.tracks[driveLog.id] = trackData;
			},
			fitBoundsCallback: () => {
				this.setNeedsFitMapBounds();
			},
			completeCallback: () => {
				this.loadNextDriveLogFromQueue();
			}
		});
	}

	private removeTrack(track: TrackData) {
		if (this.map) {
			this.map.removeLayer(track.track);
			this.map.removeLayer(track.startMarker);
			this.map.removeLayer(track.endMarker);
		}
	}

	private clearAllTracks() {
		$.each(Object.keys(this.tracks), (idx, key) => {
			this.removeTrack(this.tracks[key]);
		});
		this.tracks = {};
	}

	private setNeedsFitMapBounds() {
		debounce(() => this.fitMapBounds(), this, this.mapIsMoving ? 200 : 0)();
	}

	private fitMapBounds() {
		const keys = Object.keys(this.tracks);
		if (keys.length) {
			const firstBounds = this.tracks[keys[0]].track.getBounds();
			let north = firstBounds.getNorth();
			let east = firstBounds.getEast();
			let south = firstBounds.getSouth();
			let west = firstBounds.getWest();
			for (let i = 1; i < keys.length; i++) {
				const bounds = this.tracks[keys[i]].track.getBounds();
				north = Math.max(north, bounds.getNorth());
				east = Math.max(east, bounds.getEast());
				south = Math.min(south, bounds.getSouth());
				west = Math.min(west, bounds.getWest());
			}
			this.map.fitBounds(L.latLngBounds(L.latLng(south, west), L.latLng(north, east)));
		}
	}

	private showMap(completeCallback: (map: L.Map) => void) {
		if (!this.map) {
			this.config.container.slideDown(300, () => {
				if (!this.map) {
					this.map = L.map(this.config.container.get(0));
					L.tileLayer("https://{s}.tile.osm.org/{z}/{x}/{y}.png", {
						attribution: "&copy; <a href=\"http://osm.org/copyright\">OpenStreetMap</a> contributors"
					}).addTo(this.map);
					this.map.on("movestart", () => {
						this.mapIsMoving = true;
					}).on("moveend", () => {
						this.mapIsMoving = false;
					});
				}
				if (completeCallback) {
					completeCallback(this.map);
				}
			});
		} else if (completeCallback) {
			completeCallback(this.map);
		}
	}

	private hideMap() {
		if (this.map) {
			this.config.container.slideUp(300, () => {
				if (this.map) {
					this.map.remove();
					this.map = null;
				}
			});
		}
	}
}
